import { Component, input, output, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { Column, SetupWizardDynamicTable } from '../../../setup-wizard-dynamic-table/setup-wizard-dynamic-table';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { CommonModule } from '@angular/common';
import { BrowserService } from '../../../../../../../services/browser.service';
import { HeaderColumn } from '../../../setup-wizard-dynamic-table/header-column';
import { RowColumn } from '../../../setup-wizard-dynamic-table/row-column';
import {
	BeforeAfter,
	EMPTY_USER_CSV_TEMPLATE,
	SetupWizardBooleanType,
	SetupWizardLocation,
	SetupWizardPayload,
	SetupWizardPayloadObjectKeys,
	SetupWizardSection,
	SetupWizardUser,
	TextUtils,
	USER_CSV_LABELS,
	UserCsvKeys,
	UserCsvTemplate,
} from '@mira-avant/mtlibs';
import { SetupWizardBeforeAfter } from '../../setup-wizard-before-after/setup-wizard-before-after';
import { makeStatusColumn } from '../../setup-wizard-before-after-status/setup-wizard-before-after-status';
import { SetupWizard } from '../../../../../../../services/setup-wizard';
import { SetupWizardUserSectionsStatus } from './setup-wizard-user-sections-status/setup-wizard-user-sections-status';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { MAX_FILTER_OPTIONS_ALLOWED } from '../../setup-wizard-edit-steps';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { SetupWizardUserLocationsStatus } from './setup-wizard-user-locations-status/setup-wizard-user-locations-status';
import { SetupWizardBeforeAfterStatusWithLinks } from '../../setup-wizard-before-after-status-with-links/setup-wizard-before-after-status-with-links';

//Column to house section details for each user
export function makeSectionsColumn<T>(): Column {
	return {
		key: 'sections',
		name: 'Sections',
	};
}

//Column to house location details for each user
export function makeLocationsColumn<T>(): Column {
	return {
		key: 'locations',
		name: 'Locations',
	};
}

export function makeEnrolledColumn<T>(table?: SetupWizardUsersTable): Column {
	return {
		key: 'enrolled',
		name: 'Enrolled',
		sortOrder: null,
		// For sorting prefer the after.active property if it exists (is true or false)
		// and fall back to before.active if it is not present
		sortFn: (a: SetupWizardUser, b: SetupWizardUser) =>
			(a.after?.active ?? a.before?.active)
				?.toString()
				.localeCompare((b.after?.active ?? b.before?.active)?.toString()!) ?? -1,
		sortDirections: ['ascend', 'descend', null],
		filterMultiple: true,
		filterOptions: [...new Set([SetupWizardBooleanType.TRUE, SetupWizardBooleanType.FALSE])].map(value => ({
			text: value,
			value: value,
		})),
		filterFn: (list: string[], item: SetupWizardUser) => {
			if (!table) {
				return false;
			}

			const isEnrolled: boolean = table.isUserEnrolledInSelectedSection(item);

			//List will contain a string of 'true' or 'false' or both
			return list.includes(isEnrolled.toString());
		},
	};
}

export function makeAssignedColumn<T>(table?: SetupWizardUsersTable): Column {
	return {
		key: 'assigned',
		name: 'Assigned',
		sortOrder: null,
		// For sorting prefer the after.active property if it exists (is true or false)
		// and fall back to before.active if it is not present
		sortFn: (a: SetupWizardUser, b: SetupWizardUser) =>
			(a.after?.active ?? a.before?.active)
				?.toString()
				.localeCompare((b.after?.active ?? b.before?.active)?.toString()!) ?? -1,
		sortDirections: ['ascend', 'descend', null],
		filterMultiple: true,
		filterOptions: [...new Set([SetupWizardBooleanType.TRUE, SetupWizardBooleanType.FALSE])].map(value => ({
			text: value,
			value: value,
		})),
		filterFn: (list: string[], item: SetupWizardUser) => {
			if (!table) {
				return false;
			}

			const isAssigned: boolean = table.isUserAssignedInSelectedLocation(item);

			//List will contain a string of 'true' or 'false' or both
			return list.includes(isAssigned.toString());
		},
	};
}

export function makeHistoricalColumn<T>(table?: SetupWizardUsersTable): Column {
	return {
		key: 'historical',
		name: 'Historical',
		sortOrder: null,
		// For sorting prefer the after.active property if it exists (is true or false)
		// and fall back to before.active if it is not present
		sortFn: (a: SetupWizardUser, b: SetupWizardUser) =>
			(a.after?.active ?? a.before?.active)
				?.toString()
				.localeCompare((b.after?.active ?? b.before?.active)?.toString()!) ?? -1,
		sortDirections: ['ascend', 'descend', null],
		filterMultiple: true,
		filterOptions: [...new Set([SetupWizardBooleanType.TRUE, SetupWizardBooleanType.FALSE])].map(value => ({
			text: value,
			value: value,
		})),
		filterFn: (list: string[], item: SetupWizardUser) => {
			if (!table) {
				return false;
			}

			const isHistorical: boolean = table.isSelectedSectionHistorical(item);

			//List will contain a string of 'true' or 'false' or both
			return list.includes(isHistorical.toString());
		},
	};
}

const FULL_WIDTH_BREAKPOINT = 1100;

@Component({
	selector: 'app-setup-wizard-users-table',
	imports: [
		CommonModule,
		SetupWizardDynamicTable,
		NzIconModule,
		RowColumn,
		HeaderColumn,
		SetupWizardBeforeAfter,
		SetupWizardUserSectionsStatus,
		NzTooltipModule,
		NzSpinModule,
		NzButtonModule,
		NzPopconfirmModule,
		SetupWizardUserLocationsStatus,
		SetupWizardBeforeAfterStatusWithLinks,
	],
	standalone: true,
	templateUrl: './setup-wizard-users-table.html',
	styleUrl: './setup-wizard-users-table.scss',
})
export class SetupWizardUsersTable {
	private clientUuid: string | undefined;
	private sub?: Subscription;
	private resizeSub?: Subscription;
	public loading = signal<boolean>(true);
	public rows = signal<SetupWizardUser[]>([]);
	public pageSize = input<number>(10);
	readonly rowType = null! as BeforeAfter<UserCsvTemplate>; // type inference only
	public columns = signal<Column[]>([]);
	private wideColumns: Column[] = [];
	private narrowColumns: Column[] = [];
	public onEdit = output<SetupWizardUser>();
	public onEditSections = output<SetupWizardUser>();
	public onEditLocations = output<SetupWizardUser>();
	public onToggleDisable = output<SetupWizardUser>();
	public onRemove = output<SetupWizardUser>();
	public onReset = output<SetupWizardUser>();
	public onEnroll = output<{ user: SetupWizardUser; section: SetupWizardSection }>();
	public onUnenroll = output<{ user: SetupWizardUser; section: SetupWizardSection }>();
	public onAssign = output<{ user: SetupWizardUser; location: SetupWizardLocation }>();
	public onUnassign = output<{ user: SetupWizardUser; location: SetupWizardLocation }>();
	public enableEditButton = input<boolean>(true);
	public enableToggleDisableButton = input<boolean>(true);
	public enableRemoveButton = input<boolean>(true);
	public enableResetButton = input<boolean>(true);
	public enableEnrollButton = input<boolean>(false);
	public enableUnenrollButton = input<boolean>(false);
	public enableAssignButton = input<boolean>(false);
	public enableUnassignButton = input<boolean>(false);
	public selectedRow = input<SetupWizardUser | null>(null);
	public selectedSection = input<SetupWizardSection | null>(null);
	public selectedLocation = input<SetupWizardLocation | null>(null);
	public columnsFilter = input<string[]>([]);
	public UserCsvKeys = UserCsvKeys;
	// Users are set to upsert mode due to performance issues loading in CSVs.
	readonly UPSERT_MODE: boolean = true;
	public USER_CSV_LABELS = USER_CSV_LABELS;
	public SetupWizardBooleanType = SetupWizardBooleanType;

	private schoolIdOrEmailFilterOptions: { text: string; value: string }[] = [];
	private groupFilterOptions: { text: string; value: string }[] = [];
	private firstNameFilterOptions: { text: string; value: string }[] = [];
	private lastNameFilterOptions: { text: string; value: string }[] = [];
	private schoolIdOrEmailColumn: Column | undefined;
	private groupColumn: Column | undefined;
	private firstNameColumn: Column | undefined;
	private lastNameColumn: Column | undefined;
	private activeColumn: Column | undefined;

	constructor(
		private route: ActivatedRoute,
		private browserService: BrowserService,
		private setupWizard: SetupWizard
	) {
		this.sub = this.route.params.subscribe(params => {
			this.clientUuid = params['clientUuid'];
		});
	}

	async ngOnInit() {
		await this.initTable();

		this.sub?.add(
			this.setupWizard.preview$.subscribe(payload => {
				this.loading.set(true);

				if (payload) {
					this.updateRows(payload);
				}

				this.loading.set(false);
			})
		);
	}

	public isUserEnrolledInSelectedSection(user: SetupWizardUser): boolean {
		const userBeforeAfter = user.after ?? user.before;

		if (!userBeforeAfter) {
			return false;
		}

		const selectedSection = this.selectedSection();

		if (!selectedSection) {
			return false;
		}

		const sectionBeforeAfter = selectedSection.after ?? selectedSection.before;

		if (!sectionBeforeAfter) {
			return false;
		}

		return this.setupWizard.preview$.value.payload.usersToSections.some(userToSection => {
			const linkBeforeAfter = userToSection.after ?? userToSection.before;

			if (!linkBeforeAfter) {
				return false;
			}

			const doesLinkMatchSection =
				(linkBeforeAfter.sso_section_id &&
					sectionBeforeAfter.sso_section_id &&
					linkBeforeAfter.sso_section_id === sectionBeforeAfter.sso_section_id) ||
				(linkBeforeAfter.section_uuid &&
					sectionBeforeAfter.section_uuid &&
					linkBeforeAfter.section_uuid === sectionBeforeAfter.section_uuid);

			if (!doesLinkMatchSection) {
				return false;
			}

			const doesLinkMatchUser =
				(linkBeforeAfter.school_id_or_email &&
					userBeforeAfter.school_id_or_email &&
					linkBeforeAfter.school_id_or_email === userBeforeAfter.school_id_or_email) ||
				(linkBeforeAfter.user_uuid &&
					userBeforeAfter.user_uuid &&
					linkBeforeAfter.user_uuid === userBeforeAfter.user_uuid);

			return doesLinkMatchSection && doesLinkMatchUser;
		});
	}

	public isUserAssignedInSelectedLocation(user: SetupWizardUser): boolean {
		const userBeforeAfter = user.after ?? user.before;

		if (!userBeforeAfter) {
			return false;
		}

		const selectedLocation = this.selectedLocation();

		if (!selectedLocation) {
			return false;
		}

		const locationBeforeAfter = selectedLocation.after ?? selectedLocation.before;

		if (!locationBeforeAfter) {
			return false;
		}

		return this.setupWizard.preview$.value.payload.schoolAdminsToLocations.some(userToLocation => {
			const linkBeforeAfter = userToLocation.after ?? userToLocation.before;

			if (!linkBeforeAfter) {
				return false;
			}

			const doesLinkMatchLocation =
				(linkBeforeAfter.sso_location_id &&
					locationBeforeAfter.sso_location_id &&
					linkBeforeAfter.sso_location_id === locationBeforeAfter.sso_location_id) ||
				(linkBeforeAfter.location_uuid &&
					locationBeforeAfter.location_uuid &&
					linkBeforeAfter.location_uuid === locationBeforeAfter.location_uuid);

			if (!doesLinkMatchLocation) {
				return false;
			}

			const doesLinkMatchUser =
				(linkBeforeAfter.school_id_or_email &&
					userBeforeAfter.school_id_or_email &&
					linkBeforeAfter.school_id_or_email === userBeforeAfter.school_id_or_email) ||
				(linkBeforeAfter.user_uuid &&
					userBeforeAfter.user_uuid &&
					linkBeforeAfter.user_uuid === userBeforeAfter.user_uuid);

			return doesLinkMatchLocation && doesLinkMatchUser;
		});
	}

	// Has the selected section been enrolled with this user at any time in
	// the past or currently?
	public isSelectedSectionHistorical(user: SetupWizardUser): boolean {
		const userBeforeAfter = user.after ?? user.before;

		if (!userBeforeAfter) {
			return false;
		}

		const selectedSection = this.selectedSection();

		if (!selectedSection) {
			return false;
		}

		const sectionBeforeAfter = selectedSection.after ?? selectedSection.before;

		if (!sectionBeforeAfter) {
			return false;
		}

		return this.setupWizard.preview$.value.payload.usersToSections.some((userToSection, index) => {
			const linkBeforeAfter = userToSection.after ?? userToSection.before;

			if (!linkBeforeAfter) {
				return false;
			}

			const doesLinkMatchSection =
				(TextUtils.isNonEmptyString(linkBeforeAfter.sso_section_id) &&
					TextUtils.isNonEmptyString(sectionBeforeAfter.sso_section_id) &&
					linkBeforeAfter.sso_section_id === sectionBeforeAfter.sso_section_id) ||
				(TextUtils.isNonEmptyString(linkBeforeAfter.section_uuid) &&
					TextUtils.isNonEmptyString(sectionBeforeAfter.section_uuid) &&
					linkBeforeAfter.section_uuid === sectionBeforeAfter.section_uuid);

			if (!doesLinkMatchSection) {
				return false;
			}

			const doesLinkMatchUser =
				(TextUtils.isNonEmptyString(linkBeforeAfter.school_id_or_email) &&
					TextUtils.isNonEmptyString(userBeforeAfter.school_id_or_email) &&
					linkBeforeAfter.school_id_or_email === userBeforeAfter.school_id_or_email) ||
				(TextUtils.isNonEmptyString(linkBeforeAfter.user_uuid) &&
					TextUtils.isNonEmptyString(userBeforeAfter.user_uuid) &&
					linkBeforeAfter.user_uuid === userBeforeAfter.user_uuid);

			return doesLinkMatchSection && doesLinkMatchUser;
		});
	}

	private updateRows(payload: SetupWizardPayload) {
		this.rows.set(payload.payload.users);
		this.updateFilterOptions();
	}

	private updateFilterOptions(): void {
		const rows = this.rows();

		this.schoolIdOrEmailFilterOptions = [
			...new Set(rows.map(row => row.before?.school_id_or_email).filter(Boolean)),
		]
			.sort()
			.map(value => ({
				text: value!,
				value: value!,
			}));

		this.groupFilterOptions = [...new Set(rows.map(row => row.before?.group).filter(Boolean))]
			.sort()
			.map(value => ({
				text: value!,
				value: value!,
			}));

		this.firstNameFilterOptions = [...new Set(rows.map(row => row.before?.first_name).filter(Boolean))]
			.sort()
			.map(value => ({
				text: value!,
				value: value!,
			}));

		this.lastNameFilterOptions = [...new Set(rows.map(row => row.before?.last_name).filter(Boolean))]
			.sort()
			.map(value => ({
				text: value!,
				value: value!,
			}));

		if (this.schoolIdOrEmailFilterOptions.length > MAX_FILTER_OPTIONS_ALLOWED) {
			this.schoolIdOrEmailColumn!.filterOptions = undefined;
			this.schoolIdOrEmailColumn!.filterFn = undefined;
		} else {
			this.schoolIdOrEmailColumn!.filterOptions = this.schoolIdOrEmailFilterOptions;
			this.schoolIdOrEmailColumn!.filterFn = (value: string, row: SetupWizardUser) => {
				return row.before?.school_id_or_email === value;
			};
		}

		if (this.groupFilterOptions.length > MAX_FILTER_OPTIONS_ALLOWED) {
			this.groupColumn!.filterOptions = undefined;
			this.groupColumn!.filterFn = undefined;
		} else {
			this.groupColumn!.filterOptions = this.groupFilterOptions;
			this.groupColumn!.filterFn = (value: string, row: SetupWizardUser) => {
				return row.before?.group === value;
			};
		}

		if (this.firstNameFilterOptions.length > MAX_FILTER_OPTIONS_ALLOWED) {
			this.firstNameColumn!.filterOptions = undefined;
			this.firstNameColumn!.filterFn = undefined;
		} else {
			this.firstNameColumn!.filterOptions = this.firstNameFilterOptions;
			this.firstNameColumn!.filterFn = (value: string, row: SetupWizardUser) => {
				return row.before?.first_name === value;
			};
		}

		if (this.lastNameFilterOptions.length > MAX_FILTER_OPTIONS_ALLOWED) {
			this.lastNameColumn!.filterOptions = undefined;
			this.lastNameColumn!.filterFn = undefined;
		} else {
			this.lastNameColumn!.filterOptions = this.lastNameFilterOptions;
			this.lastNameColumn!.filterFn = (value: string, row: SetupWizardUser) => {
				return row.before?.last_name === value;
			};
		}
	}

	// This should trigger each time a new page of users becomes visible in the table
	// This functionality is unique to users because for each visible page we'll hit
	// cognito to obtain the PII but only on an as-needed basis and only for rows
	// missing the UserCsvKeys.schoolIdOrEmail column.
	public async onNewPage(visibleRows: SetupWizardUser[]) {
		if (!this.clientUuid) {
			throw new Error('Client UUID is not defined');
		}

		// 1. Update rows that have a before and are missing the school_id_or_email field
		const rowsNeedingDetails = visibleRows.filter(row => row.before && !row.before.school_id_or_email);

		if (rowsNeedingDetails.length === 0) {
			return;
		}

		// 2. Fetch details for all qualifying rows concurrently, with error handling per promise
		const detailPromises = rowsNeedingDetails.map(async row => {
			try {
				if (!row.before!.user_uuid) {
					throw new Error('User UUID is not defined for row');
				}

				const userDetails: UserCsvTemplate | undefined = await this.setupWizard.getUserCognitoDetails$(
					this.clientUuid!,
					row.before!.user_uuid!
				);

				if (!userDetails) {
					throw new Error('Failed to fetch user details from Cognito');
				}

				return {
					row,
					updatedBefore: { ...row.before!, ...userDetails } as UserCsvTemplate,
					success: true,
				};
			} catch (error) {
				return { row, success: false };
			}
		});

		// 3. Wait for all fetches to complete (some may have failed)
		const results = await Promise.all(detailPromises);

		// 4. Collect only the successful updates
		const updatedBefores: UserCsvTemplate[] = results
			.filter((r): r is { row: SetupWizardUser; updatedBefore: UserCsvTemplate; success: true } => r.success)
			.map(r => r.updatedBefore);

		// 5. Apply updates in one batch call (only if we have successful results)
		if (updatedBefores.length > 0) {
			this.setupWizard.updateExistingPayloadItems(SetupWizardPayloadObjectKeys.users, updatedBefores);
		}

		// Optional: log summary of what happened
		const failedCount = results.length - updatedBefores.length;
		if (failedCount > 0) {
			console.warn(
				`Cognito details fetch (or cached): ${updatedBefores.length} succeeded, ${failedCount} failed`
			);
		}
	}

	public async initTable(): Promise<void> {
		try {
			this.initColumns();

			this.resizeSub?.unsubscribe();
			this.resizeSub = this.browserService.onResize$.subscribe((width: number) => {
				if (width >= FULL_WIDTH_BREAKPOINT) {
					this.columns.set(this.wideColumns);
				} else {
					this.columns.set(this.narrowColumns);
				}
			});
		} catch (error) {
			console.error('Error loading data:', error);
		}
	}

	private initColumns(): void {
		Object.keys(EMPTY_USER_CSV_TEMPLATE).map(key => {
			switch (key) {
				case UserCsvKeys.schoolIdOrEmail:
					this.schoolIdOrEmailColumn = {
						key,
						name: USER_CSV_LABELS[UserCsvKeys.schoolIdOrEmail],
						sortOrder: null,
						sortFn: (a: SetupWizardUser, b: SetupWizardUser) =>
							a.before?.school_id_or_email!.localeCompare(b.before?.school_id_or_email!) ?? -1,
						sortDirections: ['ascend', 'descend', null],
						filterMultiple: true,
					};
					break;
				case UserCsvKeys.group:
					this.groupColumn = {
						key,
						name: USER_CSV_LABELS[UserCsvKeys.group],
						sortOrder: null,
						sortFn: (a: SetupWizardUser, b: SetupWizardUser) =>
							a.before?.group!.localeCompare(b.before?.group!) ?? -1,
						sortDirections: ['ascend', 'descend', null],
						filterMultiple: true,
					};
					break;
				case UserCsvKeys.firstName:
					this.firstNameColumn = {
						key,
						name: USER_CSV_LABELS[UserCsvKeys.firstName],
						sortOrder: null,
						sortFn: (a: SetupWizardUser, b: SetupWizardUser) =>
							a.before?.first_name!.localeCompare(b.before?.first_name!) ?? -1,
						sortDirections: ['ascend', 'descend', null],
						filterMultiple: true,
					};
					break;
				case UserCsvKeys.lastName:
					this.lastNameColumn = {
						key,
						name: USER_CSV_LABELS[UserCsvKeys.lastName],
						sortOrder: null,
						sortFn: (a: SetupWizardUser, b: SetupWizardUser) =>
							a.before?.last_name!.localeCompare(b.before?.last_name!) ?? -1,
						sortDirections: ['ascend', 'descend', null],
						filterMultiple: true,
					};
					break;
				case UserCsvKeys.active:
					this.activeColumn = {
						key,
						name: USER_CSV_LABELS[UserCsvKeys.active],
						sortOrder: null,
						// For sorting prefer the after.active property if it exists (is true or falne)
						// and fall back to before.active if it is not present
						sortFn: (a: SetupWizardUser, b: SetupWizardUser) =>
							(a.after?.active ?? a.before?.active)
								?.toString()
								.localeCompare((b.after?.active ?? b.before?.active)?.toString()!) ?? -1,
						sortDirections: ['ascend', 'descend', null],
						filterMultiple: true,
						filterOptions: [...new Set([SetupWizardBooleanType.TRUE, SetupWizardBooleanType.FALSE])].map(
							value => ({
								text: value,
								value: value,
							})
						),
						filterFn: (list: string[], item: SetupWizardUser) =>
							list.some(
								name => (item.after?.active ?? item.before?.active)?.toString()?.indexOf(name) !== -1
							),
					};
					break;
				default:
					break;
			}
		});

		if (
			!this.schoolIdOrEmailColumn ||
			!this.groupColumn ||
			!this.firstNameColumn ||
			!this.lastNameColumn ||
			!this.activeColumn
		) {
			throw new Error('One or more columns are not defined');
		}

		const statusColumn: Column = makeStatusColumn<SetupWizardUser>(this.UPSERT_MODE);

		const sectionsColumn: Column = makeSectionsColumn<SetupWizardUser>();

		const locationsColumn: Column = makeLocationsColumn<SetupWizardUser>();

		this.wideColumns = [
			this.schoolIdOrEmailColumn,
			this.groupColumn,
			this.firstNameColumn,
			this.lastNameColumn,
			this.activeColumn,
			sectionsColumn,
			locationsColumn,
			statusColumn,
		];

		// If the caller wants the enrolled column
		if (this.columnsFilter().length > 0 && this.columnsFilter().includes(makeEnrolledColumn().key)) {
			this.wideColumns.push(makeEnrolledColumn(this));
		}

		// If the caller wants the historical column
		if (this.columnsFilter().length > 0 && this.columnsFilter().includes(makeHistoricalColumn().key)) {
			this.wideColumns.push(makeHistoricalColumn(this));
		}

		this.wideColumns = this.wideColumns.filter(column =>
			this.columnsFilter().length > 0 ? this.columnsFilter().includes(column.key) : true
		);

		this.narrowColumns = [this.firstNameColumn, this.lastNameColumn, statusColumn].filter(column =>
			this.columnsFilter().length > 0 ? this.columnsFilter().includes(column.key) : true
		);

		this.columns.set(this.wideColumns);
	}

	onEditClick(row: SetupWizardUser) {
		this.onEdit.emit(row);
	}

	onEditSectionsClick(row: SetupWizardUser) {
		this.onEditSections.emit(row);
	}

	onEditLocationsClick(row: SetupWizardUser) {
		this.onEditLocations.emit(row);
	}

	onToggleDisableClick(row: SetupWizardUser) {
		this.onToggleDisable.emit(row);
	}

	onRemoveClick(row: SetupWizardUser) {
		this.onRemove.emit(row);
	}

	onResetClick(row: SetupWizardUser) {
		this.onReset.emit(row);
	}

	onEnrollClick(row: SetupWizardUser) {
		const section = this.selectedSection();

		if (!section) {
			throw new Error('No section selected');
		}

		this.onEnroll.emit({ user: row, section });
	}

	onUnenrollClick(row: SetupWizardUser) {
		const section = this.selectedSection();

		if (!section) {
			throw new Error('No section selected');
		}

		this.onUnenroll.emit({ user: row, section });
	}

	onAssignClick(row: SetupWizardUser) {
		const location = this.selectedLocation();

		if (!location) {
			throw new Error('No location selected');
		}

		this.onAssign.emit({ user: row, location });
	}

	onUnassignClick(row: SetupWizardUser) {
		const location = this.selectedLocation();

		if (!location) {
			throw new Error('No location selected');
		}

		this.onUnassign.emit({ user: row, location });
	}

	ngOnDestroy() {
		this.sub?.unsubscribe();
		this.resizeSub?.unsubscribe();
	}
}
